import { ListChecksIcon, PiggyBankIcon, SproutIcon } from 'lucide-react'
import type { PillarPageContent } from '../../types/pillarPage'

export const finanzasPage: PillarPageContent = {
  slug: 'finanzas',
  accent: { base: '47 125 91', deep: '16 37 27', ink: '244 242 238' },
  affiliateUrl: '',

  hero: {
    eyebrow: 'Finanzas · CreceElite',
    headlineTop: 'El dinero no es tu meta.',
    headlineAccent: 'Es una herramienta de tu propósito.',
    quote:
      '“La riqueza con propósito comienza cuando aprendes a administrar con sabiduría lo que Dios puso en tus manos.”',
    ctaLabel: 'Ordenar mis finanzas',
    tags: ['Orden', 'Ahorro', 'Ingresos', 'Libertad'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e8ed6f2c-669c-4e47-8920-448811dbfc43.jpg',
    imageAlt: 'Persona planificando en un cuaderno sobre un escritorio en una sala oscura',
  },

  perspective: {
    eyebrow: 'Cambio de perspectiva',
    titleTop: 'No se trata de ganar más.',
    titleSub: 'Se trata de administrar mejor.',
    paragraphs: [
      'Puedes aumentar tus ingresos y seguir viviendo con la misma tensión de siempre. El problema casi nunca es solamente cuánto entra, sino qué haces con lo que ya tienes.',
      'Tu dinero refleja tus decisiones: lo que priorizas, lo que postergas y aquello a lo que le has dado autoridad sobre tu tranquilidad.',
      'Por eso, en CreceElite no vemos las finanzas como una carrera por acumular ni como una promesa de riqueza rápida.',
    ],
    closingParagraph: 'Las vemos como una forma de administración.',
    highlight: ['Ordena tus finanzas.', 'Fortalece tu capacidad.', 'Construye con propósito.'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/602c3644-b951-4283-874b-cf4d4921898d.jpg',
    imageAlt: 'Cuaderno abierto y papeles ordenados sobre un escritorio oscuro',
    caption: 'Administración, no acumulación',
  },

  foundation: {
    eyebrow: 'La mayordomía',
    title: 'Tu dinero merece atención porque tu propósito requiere recursos.',
    quote: '“El que es fiel en lo poco, también lo es en lo mucho.”',
    quoteSource: 'Lucas 16:10',
    lead: 'El objetivo no es perseguir una cifra.',
    closing:
      'El objetivo es desarrollar una administración capaz de sostener la vida, la familia y la misión que Dios puso delante de ti.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/5eec1e4a-46a2-47bd-bd1d-cfd4b2c07bf1.jpg',
    imageAlt: 'Vista de una ciudad tranquila al amanecer desde una ventana en una sala oscura',
    virtues: [
      {
        label: 'Orden',
        text: 'Saber con claridad cuánto entra, cuánto sale y hacia dónde va.',
        icon: ListChecksIcon,
      },
      {
        label: 'Dominio propio',
        text: 'Decidir con criterio en lugar de comprar por impulso.',
        icon: PiggyBankIcon,
      },
      {
        label: 'Visión',
        text: 'Sembrar hoy pensando en los años que vienen, no solo en el mes.',
        icon: SproutIcon,
      },
    ],
  },

  pillars: {
    eyebrow: 'Los 3 pilares',
    title: 'Una vida financiera sólida se construye sobre tres fundamentos.',
    items: [
      {
        number: '01',
        label: 'Orden',
        headline: 'No puedes administrar lo que no conoces.',
        text: 'Un presupuesto no es una jaula: es claridad. Saber a dónde va tu dinero cada mes es el primer paso para dejar de vivir en reacción y comenzar a decidir con intención.',
      },
      {
        number: '02',
        label: 'Ingresos',
        headline: 'Tu capacidad de generar también se desarrolla.',
        text: 'Aumentar tus ingresos no depende de la suerte, sino de habilidades, valor entregado y decisiones sostenidas en el tiempo. Aprender a crear valor cambia tu techo.',
      },
      {
        number: '03',
        label: 'Multiplicación',
        headline: 'Lo que se administra bien, crece.',
        text: 'Ahorrar, invertir y emprender no son atajos para hacerse rico rápido. Son formas de darle propósito a largo plazo a lo que ya está en tus manos.',
      },
    ],
  },

  purpose: {
    eyebrow: 'El verdadero objetivo',
    title: '¿Para qué quieres finanzas sanas?',
    reasons: [
      { label: 'Para tener paz', text: 'Para dejar de vivir con la tensión de fin de mes.' },
      { label: 'Para tu familia', text: 'Para dar estabilidad y no solamente promesas.' },
      { label: 'Para tu trabajo', text: 'Para decidir desde la libertad y no desde la urgencia.' },
      {
        label: 'Para tu futuro',
        text: 'Para llegar a los próximos años con opciones y no con deudas.',
      },
    ],
    statementLead: 'No administras tu dinero solamente para tener más.',
    statementAccent: 'Lo administras para vivir con libertad y propósito.',
  },

  discipline: {
    eyebrow: 'Disciplina financiera',
    titleTop: 'El entusiasmo te inicia.',
    titleAccent: 'La administración te transforma.',
    paragraphs: [
      'Habrá meses en los que querrás abandonar el presupuesto.',
      'Días en los que gastar será más fácil que esperar.',
    ],
    closingParagraph:
      'Pero la libertad financiera llega cuando tus decisiones dejan de depender del impulso del momento.',
    sequence: ['Claridad', 'Decisión', 'Hábito', 'Libertad'],
    closingLead: 'No necesitas hacerlo perfecto.',
    closingAccent: 'Necesitas hacerlo consistentemente.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/c67ac528-0c81-4e87-bb69-3c4c86aa61f8.jpg',
  },

  path: {
    eyebrow: 'Ahora necesitas un camino',
    title: 'Ahora necesitas un camino.',
    paragraphs: [
      'Saber que necesitas ordenar tus finanzas es solamente el comienzo.',
      'El siguiente paso es aprender qué hacer, cómo hacerlo y cómo mantenerlo en el tiempo.',
    ],
    steps: [
      { label: 'Presupuesto', text: 'Aprende a ordenar tus ingresos y tus gastos con claridad.' },
      { label: 'Ahorro', text: 'Construye un fondo que te dé margen y tranquilidad.' },
      { label: 'Deudas', text: 'Diseña un plan realista para salir y no volver.' },
      { label: 'Ingresos', text: 'Desarrolla habilidades que aumenten tu capacidad de generar.' },
      { label: 'Inversión', text: 'Aprende a hacer crecer lo que administras con criterio.' },
    ],
  },

  formation: {
    eyebrow: 'El siguiente paso',
    title: 'No tienes que descubrirlo todo solo.',
    paragraphs: [
      'Una de las mejores decisiones que puedes tomar es aprender de personas que ya han recorrido este camino.',
      'Por eso, hemos seleccionado una formación que puede ayudarte a ordenar tus finanzas, mejorar tus decisiones y construir riqueza con propósito y a largo plazo.',
    ],
    cardEyebrow: 'Formación recomendada',
    cardTitle: 'Desarrolla tus finanzas',
    cardQuote: '“Aprende. Ordena. Multiplica. Repite.”',
    highlights: ['Aprende.', 'Ordena.', 'Multiplica.', 'Repite.'],
    ctaLabel: 'Conocer el método',
    note: 'Una recomendación de CreceElite para quienes quieren criterio, no promesas rápidas.',
  },

  identity: {
    eyebrow: 'Identidad',
    title: '¿Quién estás decidiendo ser?',
    before: [
      'Gastar por impulso',
      'Vivir al límite',
      'Sin presupuesto',
      'Deudas sin plan',
      'Depender de la suerte',
    ],
    after: ['Criterio', 'Margen', 'Orden', 'Plan claro', 'Administración'],
    statementLead: 'La transformación financiera comienza mucho antes de que cambie tu cuenta.',
    statementAccent: 'Comienza cuando cambia tu manera de administrar.',
  },

  manifesto: {
    eyebrow: 'Manifiesto CreceElite',
    title: 'Mis finanzas son mi responsabilidad',
    lines: [
      'No administro por ambición. Administro porque tengo un propósito.',
      'No busco atajos. Busco criterio.',
      'No gasto por impulso. Decido con intención.',
      'No dependo de la suerte. Construyo hábitos.',
      'No busco solamente tener más. Quiero ser fiel con lo que tengo.',
      'Porque todavía tengo mucho por vivir. Mucho por construir. Mucho por entregar.',
    ],
  },

  finalCta: {
    headlineTop: 'El dinero es la herramienta.',
    headlineAccent: 'Tu propósito es el destino.',
    paragraph:
      'Ahora que entiendes la importancia de administrar bien, el siguiente paso es desarrollar un sistema financiero que puedas llevar a la práctica.',
    ctaLabel: 'Comenzar mi camino',
    subnote: 'Accede a la formación recomendada por CreceElite.',
  },

  stickyCtaLabel: 'Comenzar mi camino',
}
